import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import './AppVersions.css';
import { supAppAddVersion, supAppGetVersions, supAppUpdateVersion } from '../../api/supportAdmin';
import { useSearchParams } from 'react-router-dom';
import Pagination from '../../components/pagination/Pagination';
import { toast } from 'react-toastify';

export default function AppVersions() {
    const [versions, setVersions] = useState([]);
    const [platform, setPlatform] = useState('all');
    const [loading, setLoading] = useState(false);
    const [searchParams, setSearchParams] = useSearchParams();
    const limit = Number(searchParams.get('limit')) || 10;
    const [offset, setOffset] = useState(Number(searchParams.get('offset')) || 0);
    const pageNumber = Math.floor(offset / limit) + 1;
    const [totalCount, setTotalCount] = useState(0);
    const totalPages = Math.ceil(totalCount / limit);
    const [showModal, setShowModal] = useState(false);
    const [editingVersion, setEditingVersion] = useState(null);

    const [formData, setFormData] = useState({
        version: '',
        platform: 'android',
        forceUpdate: false,
        minSupportedVersion: '',
        releaseNotes: '',
        isActive: false
    });

    const getVersions = async () => {
        try {
            setLoading(true);

            const params = {
                limit,
                offset
            };

            if (platform !== 'all') {
                params.platform = platform;
            }

            const response = await supAppGetVersions(params);

            setVersions(response?.data?.data || []);
            setTotalCount(response?.data?.total);
        } catch (error) {
            console.error('Failed to get app versions', error);
        } finally {
            setLoading(false);
        }
    };

    const updateURL = useCallback(
        (params) => {
            const current = Object.fromEntries([...searchParams]);

            Object.keys(params).forEach((key) => {
                if (params[key] === '' || params[key] === null) {
                    delete current[key];
                } else {
                    current[key] = params[key];
                }
            });
            setSearchParams(current);
        },
        [searchParams, setSearchParams]
    );

    useEffect(() => {
        if (!totalPages) return;

        if (pageNumber > totalPages) {
            updateURL({ page: totalPages });
        }
        //eslint-disable-next-line
    }, [totalPages, pageNumber, updateURL]);

    useEffect(() => {
        getVersions();
    }, [platform]);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;

        setFormData((prev) => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const openAddModal = () => {
        setEditingVersion(null);

        setFormData({
            version: '',
            platform: 'android',
            forceUpdate: false,
            minSupportedVersion: '',
            releaseNotes: '',
            isActive: false
        });

        setShowModal(true);
    };

    const openEditModal = (item) => {
        setEditingVersion(item);

        setFormData({
            version: item.version || '',
            platform: item.platform || 'android',
            forceUpdate: item.forceUpdate || false,
            minSupportedVersion: item.minSupportedVersion || '',
            releaseNotes: item.releaseNotes || '',
            isActive: item.isActive || false
        });

        setShowModal(true);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        try {
            if (editingVersion) {
                await supAppUpdateVersion(editingVersion._id, {
                    forceUpdate: formData.forceUpdate,
                    minSupportedVersion: formData.minSupportedVersion || null,
                    releaseNotes: formData.releaseNotes,
                    isActive: formData.isActive
                });
            } else {
                await supAppAddVersion(formData);
            }

            setShowModal(false);
            getVersions();
        } catch (error) {
            console.error('Failed to save app version', error);

            toast.error(error?.response?.data?.message || 'Failed to save app version');
        }
    };

    const paginationClick = (number, offSetNumber) => {
        if (Math.ceil(totalCount / limit) > 1) {
            updateURL({ page: number });
            window.scrollTo({
                top: 0,
                behavior: 'smooth'
            });
        }
    };

    return (
        <div className='appVersionsPage'>
            <div className='appVersionsHeader'>
                <div>
                    <h2>App Versions</h2>
                    <p>Manage Android and iOS app versions</p>
                </div>

                <button className='sendBtn' onClick={openAddModal}>
                    + Add Version
                </button>
            </div>

            <div className='appVersionFilter'>
                <label>Platform : </label>

                <select value={platform} onChange={(e) => setPlatform(e.target.value)} className='select'>
                    <option value='all'>All</option>
                    <option value='android'>Android</option>
                    <option value='ios'>iOS</option>
                </select>
            </div>

            <div className='appVersionsTableWrapper'>
                <table className='appVersionsTable'>
                    <thead>
                        <tr>
                            <th>Platform</th>
                            <th>Version</th>
                            <th>Status</th>
                            <th>Minimum Supported</th>
                            <th>Force Update</th>
                            <th>Released</th>
                            <th>Actions</th>
                        </tr>
                    </thead>

                    <tbody>
                        {loading ? (
                            <tr>
                                <td colSpan='7' className='tableMessage'>
                                    Loading...
                                </td>
                            </tr>
                        ) : versions.length === 0 ? (
                            <tr>
                                <td colSpan='7' className='tableMessage'>
                                    No app versions found
                                </td>
                            </tr>
                        ) : (
                            versions.map((item) => (
                                <tr key={item._id}>
                                    <td>
                                        <span className='platformBadge'>{item.platform}</span>
                                    </td>

                                    <td className='versionValue'>{item.version}</td>

                                    <td>
                                        <span className={item.isActive ? 'statusBadge active' : 'statusBadge inactive'}>
                                            {item.isActive ? 'Active' : 'Inactive'}
                                        </span>
                                    </td>

                                    <td>{item.minSupportedVersion || '-'}</td>

                                    <td>
                                        {item.forceUpdate ? (
                                            <span className='forceBadge'>Required</span>
                                        ) : (
                                            <span className='optionalBadge'>Optional</span>
                                        )}
                                    </td>

                                    <td>{item.publishedAt ? new Date(item.publishedAt).toLocaleDateString() : '-'}</td>

                                    <td>
                                        <button className='editVersionBtn' onClick={() => openEditModal(item)}>
                                            Edit
                                        </button>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>

            {showModal && (
                <div className='versionModalOverlay' onClick={() => setShowModal(false)}>
                    <div className='versionModal' onClick={(e) => e.stopPropagation()}>
                        <div className='versionModalHeader'>
                            <div>
                                <h3>{editingVersion ? 'Edit App Version' : 'Add App Version'}</h3>
                            </div>

                            <button className='closeModalBtn' onClick={() => setShowModal(false)}>
                                ×
                            </button>
                        </div>

                        <form onSubmit={handleSubmit}>
                            <div className='versionFormGroup'>
                                <label>Platform</label>

                                <select
                                    name='platform'
                                    value={formData.platform}
                                    onChange={handleChange}
                                    disabled={!!editingVersion}
                                >
                                    <option value='android'>Android</option>

                                    <option value='ios'>iOS</option>
                                </select>
                            </div>

                            <div className='versionFormGroup'>
                                <label>Version</label>

                                <input
                                    type='text'
                                    name='version'
                                    value={formData.version}
                                    onChange={handleChange}
                                    placeholder='e.g. 1.4.0'
                                    required
                                    disabled={!!editingVersion}
                                />
                            </div>

                            <div className='versionFormGroup'>
                                <label>Minimum Supported Version</label>

                                <input
                                    type='text'
                                    name='minSupportedVersion'
                                    value={formData.minSupportedVersion}
                                    onChange={handleChange}
                                    placeholder='e.g. 1.2.0'
                                />
                            </div>

                            <div className='versionCheckboxRow'>
                                <label>
                                    <input
                                        type='checkbox'
                                        name='forceUpdate'
                                        checked={formData.forceUpdate}
                                        onChange={handleChange}
                                    />

                                    <span>Force Update</span>
                                </label>

                                <label>
                                    <input
                                        type='checkbox'
                                        name='isActive'
                                        checked={formData.isActive}
                                        onChange={handleChange}
                                    />

                                    <span>Make Active</span>
                                </label>
                            </div>

                            <div className='versionFormGroup'>
                                <label>Release Notes</label>

                                <textarea
                                    name='releaseNotes'
                                    value={formData.releaseNotes}
                                    onChange={handleChange}
                                    placeholder="What's new in this version?"
                                    rows='4'
                                />
                            </div>

                            <div className='versionModalActions'>
                                <button type='button' className='cancelVersionBtn' onClick={() => setShowModal(false)}>
                                    Cancel
                                </button>

                                <button type='submit' className='sendBtn'>
                                    {editingVersion ? 'Update Version' : 'Create Version'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
            <Pagination
                paginationClick={paginationClick}
                numberOfRows={totalCount}
                offSet={offset}
                pageNumber={pageNumber}
                limit={limit}
            />
        </div>
    );
}
